import { ReactNode } from "react";
import BoringAvatar from "boring-avatars";
import { cn } from "~/lib/utils";
import { AGENT_BRAND_COLORS } from "~/services/agents";

// Temporary: render boring-avatars (matching the idiom used in AgentCard on
// main) while the line-art set is parked. All SHAPES/TINTS/helpers below are
// kept intentionally so we can flip back by restoring the original render body.
const BORING_GRAYSCALE = ["#2a2a2a", "#5a5b5d", "#9a9b9d", "#cfd0d2", "#eeeeef"];

// Hand-drawn line-art avatars rendered into a 64x64 viewBox.
// Stroke uses the SVG's `stroke` attribute; small filled accents reference
// `currentColor` (set on the wrapping span) so a single tint controls both.

const SHAPES: Record<string, ReactNode> = {
  // curly hair + round glasses
  riley: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M18 30 Q18 14 32 14 Q46 14 46 30 Q46 42 32 44 Q18 42 18 30 Z" />
      <path d="M17 22 Q14 14 20 12 Q22 8 28 10 Q31 6 35 9 Q40 7 43 12 Q49 13 48 22" />
      <path d="M20 18 Q23 20 26 18 M30 16 Q33 18 36 16 M40 18 Q43 20 46 18" />
      <circle cx="26" cy="30" r="3.2" />
      <circle cx="38" cy="30" r="3.2" />
      <path d="M29.2 30 L34.8 30" />
      <path d="M32 32 Q31 35 32 36" />
      <path d="M28 38 Q32 40 36 38" />
      <path d="M17 30 Q15 32 17 34" />
      <path d="M47 30 Q49 32 47 34" />
    </g>
  ),

  // side part + stubble
  marcus: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 60 Q22 46 32 46 Q42 46 56 60" />
      <path d="M27 48 L27 42 M37 48 L37 42" />
      <path d="M18 30 Q18 14 32 14 Q46 14 46 30 Q46 42 32 44 Q18 42 18 30 Z" />
      <path d="M19 20 Q22 10 34 11 Q44 12 46 20" />
      <path d="M24 16 L34 13" />
      <path d="M30 13 L40 15" />
      <path d="M23 26 L28 25" />
      <path d="M36 25 L41 26" />
      <circle cx="26" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 31 L31 35 L33 35" />
      <path d="M27 39 Q32 41 37 39" />
      <circle cx="24" cy="39" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="26" cy="41" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="29" cy="42" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="32" cy="43" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="35" cy="42" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="38" cy="41" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="40" cy="39" r="0.4" fill="currentColor" stroke="none" />
      <path d="M17 30 Q15 32 17 34" />
      <path d="M47 30 Q49 32 47 34" />
    </g>
  ),

  // long hair + hoop earring
  anika: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 60 Q14 44 18 36" />
      <path d="M52 60 Q50 44 46 36" />
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 16 32 16 Q44 16 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      <path d="M20 24 Q22 12 32 12 Q42 12 44 24" />
      <path d="M28 13 Q30 16 31 20" />
      <path d="M20 28 Q16 40 18 52" />
      <path d="M44 28 Q48 40 46 52" />
      <path d="M24 26 Q26 25 28 26" />
      <path d="M36 26 Q38 25 40 26" />
      <path d="M25 29 Q26 30.2 27 29" />
      <path d="M37 29 Q38 30.2 39 29" />
      <path d="M32 31 Q31 35 32 36" />
      <path d="M28 39 Q32 41 36 39" />
      <circle cx="19" cy="36" r="1.4" />
      <path d="M45 32 Q47 34 45 36" />
    </g>
  ),

  // Manifold mascot — angular "M" inside a rounded badge
  manny: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <rect x="12" y="16" width="40" height="40" rx="11" />
      <line x1="32" y1="16" x2="32" y2="9" />
      <circle cx="32" cy="7" r="2" fill="currentColor" stroke="none" />
      <path d="M19 44 L19 26 L26 38 L32 28 L38 38 L45 26 L45 44" />
      <circle cx="26" cy="46" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="38" cy="46" r="1.4" fill="currentColor" stroke="none" />
    </g>
  ),

  // beanie + goatee
  devon: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 20 32 20 Q44 20 44 32 Q44 44 32 46 Q20 44 20 32 Z" />
      <path d="M17 22 Q17 10 32 10 Q47 10 47 22 L47 24 L17 24 Z" />
      <path d="M17 24 L47 24" />
      <path d="M22 14 L22 22" />
      <path d="M28 12 L28 22" />
      <path d="M36 12 L36 22" />
      <path d="M42 14 L42 22" />
      <circle cx="32" cy="8" r="2" />
      <path d="M24 28 L28 28" />
      <path d="M36 28 L40 28" />
      <circle cx="26" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 33 L31 37 L33 37" />
      <path d="M28 40 Q32 38 36 40" />
      <path d="M30 42 Q32 45 34 42" />
      <path d="M31 44 L31 47" />
      <path d="M33 44 L33 47" />
      <path d="M19 32 Q17 34 19 36" />
      <path d="M45 32 Q47 34 45 36" />
    </g>
  ),

  // side bun + soft smile
  sage: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 16 32 16 Q44 16 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* hairline pulled back */}
      <path d="M20 24 Q22 14 32 13 Q42 14 44 24" />
      <path d="M22 22 Q28 18 34 19" />
      {/* bun on left */}
      <circle cx="14" cy="20" r="5" />
      <path d="M11 18 Q14 21 17 18" />
      <path d="M19 22 Q16 21 15 24" />
      {/* eyebrows */}
      <path d="M24 27 Q26 26 28 27" />
      <path d="M36 27 Q38 26 40 27" />
      {/* eyes */}
      <circle cx="26" cy="30" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="30" r="0.9" fill="currentColor" stroke="none" />
      {/* nose */}
      <path d="M32 32 Q31 35 32 36" />
      {/* smile */}
      <path d="M28 39 Q32 41 36 39" />
      <path d="M45 32 Q47 34 45 36" />
    </g>
  ),

  // chin-length bob + bangs
  juno: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 16 32 16 Q44 16 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* straight blunt bangs */}
      <path d="M18 22 L46 22" />
      <path d="M18 22 Q18 14 32 12 Q46 14 46 22" />
      {/* bob falls to jawline */}
      <path d="M18 22 Q16 34 19 44" />
      <path d="M46 22 Q48 34 45 44" />
      {/* eyebrows */}
      <path d="M23 27 L28 27" />
      <path d="M36 27 L41 27" />
      {/* eyes — almond */}
      <path d="M25 30 Q26 31 27 30" />
      <path d="M37 30 Q38 31 39 30" />
      {/* tiny stud earring */}
      <circle cx="19" cy="36" r="0.7" fill="currentColor" stroke="none" />
      <circle cx="45" cy="36" r="0.7" fill="currentColor" stroke="none" />
      {/* nose */}
      <path d="M32 32 Q31 35 32 36" />
      {/* slight smirk */}
      <path d="M28 39 Q32 40 36 39" />
    </g>
  ),

  // bald + mustache + hoop earring
  felix: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      {/* head — fully exposed crown */}
      <path d="M18 30 Q18 12 32 12 Q46 12 46 30 Q46 42 32 44 Q18 42 18 30 Z" />
      {/* shine on head */}
      <path d="M24 18 Q28 14 32 14" />
      {/* eyebrows — heavier */}
      <path d="M23 26 Q26 25 29 26" />
      <path d="M35 26 Q38 25 41 26" />
      {/* eyes */}
      <circle cx="26" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="29" r="0.9" fill="currentColor" stroke="none" />
      {/* nose */}
      <path d="M32 31 L31 35 L33 35" />
      {/* mustache */}
      <path d="M26 38 Q28 36 32 37 Q36 36 38 38" />
      <path d="M26 38 Q28 40 31 39" />
      <path d="M38 38 Q36 40 33 39" />
      {/* mouth */}
      <path d="M30 41 Q32 42 34 41" />
      {/* ears + hoop */}
      <path d="M17 30 Q15 32 17 34" />
      <path d="M47 30 Q49 32 47 34" />
      <circle cx="48" cy="36" r="1.6" />
    </g>
  ),

  // pixie cut + dangling earrings
  nova: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 18 32 18 Q44 18 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* pixie cut — short choppy layers */}
      <path d="M19 22 Q22 12 32 12 Q42 12 45 22" />
      <path d="M22 16 L25 22" />
      <path d="M28 13 L29 22" />
      <path d="M34 13 L34 22" />
      <path d="M40 16 L38 22" />
      <path d="M19 22 L19 28" />
      <path d="M45 22 L45 28" />
      {/* eyebrows */}
      <path d="M24 27 Q26 26 28 27" />
      <path d="M36 27 Q38 26 40 27" />
      {/* eyes */}
      <circle cx="26" cy="30" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="30" r="0.9" fill="currentColor" stroke="none" />
      {/* nose */}
      <path d="M32 32 Q31 35 32 36" />
      {/* smile */}
      <path d="M28 39 Q32 41 36 39" />
      {/* dangle earrings */}
      <path d="M19 35 L19 39" />
      <circle cx="19" cy="40.5" r="1.2" fill="currentColor" stroke="none" />
      <path d="M45 35 L45 39" />
      <circle cx="45" cy="40.5" r="1.2" fill="currentColor" stroke="none" />
    </g>
  ),

  // robot helper — round head + antennae
  pixel: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      {/* body / collar */}
      <path d="M14 60 Q22 50 32 50 Q42 50 50 60" />
      {/* neck bolt */}
      <path d="M29 50 L29 46 M35 50 L35 46" />
      {/* head — rounded square */}
      <rect x="14" y="14" width="36" height="32" rx="9" />
      {/* face screen inset */}
      <rect x="19" y="22" width="26" height="14" rx="3" />
      {/* eyes — squares with pupils */}
      <rect x="23" y="26" width="5" height="5" rx="1" />
      <rect x="36" y="26" width="5" height="5" rx="1" />
      <circle cx="25.5" cy="28.5" r="0.7" fill="currentColor" stroke="none" />
      <circle cx="38.5" cy="28.5" r="0.7" fill="currentColor" stroke="none" />
      {/* mouth — pixel grin */}
      <path d="M26 40 L28 40 L28 42 L30 42 L30 40 L34 40 L34 42 L36 42 L36 40 L38 40" />
      {/* antennae */}
      <line x1="22" y1="14" x2="20" y2="8" />
      <circle cx="20" cy="7" r="1.6" fill="currentColor" stroke="none" />
      <line x1="42" y1="14" x2="44" y2="8" />
      <circle cx="44" cy="7" r="1.6" fill="currentColor" stroke="none" />
      {/* side panels */}
      <path d="M14 28 L11 28 L11 34 L14 34" />
      <path d="M50 28 L53 28 L53 34 L50 34" />
    </g>
  ),

  // afro halo + hoop earring
  mira: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 22 32 22 Q44 22 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* afro cloud */}
      <path d="M12 26 Q8 16 18 11 Q20 4 28 7 Q32 3 36 7 Q44 4 46 11 Q56 16 52 26" />
      <path d="M14 20 Q16 18 18 20" />
      <path d="M20 14 Q22 12 25 14" />
      <path d="M28 10 Q31 8 34 10" />
      <path d="M38 12 Q41 11 44 14" />
      <path d="M46 18 Q48 17 50 20" />
      <path d="M16 26 Q18 24 20 26" />
      <path d="M44 26 Q47 24 49 26" />
      <path d="M24 28 Q26 27 28 28" />
      <path d="M36 28 Q38 27 40 28" />
      <circle cx="26" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 33 Q31 36 32 37" />
      <path d="M28 40 Q32 42 36 40" />
      <circle cx="20" cy="36" r="1.2" />
    </g>
  ),

  // headband + ponytail behind
  theo: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M18 30 Q18 14 32 14 Q46 14 46 30 Q46 42 32 44 Q18 42 18 30 Z" />
      {/* hair top */}
      <path d="M18 22 Q18 12 32 12 Q46 12 46 22" />
      {/* headband band */}
      <path d="M18 22 L46 22" />
      <path d="M18 20 L46 20" />
      {/* ponytail tail */}
      <path d="M46 26 Q51 30 51 38 Q49 42 45 42" />
      <path d="M46 30 L50 33" />
      <path d="M46 34 L49 37" />
      <path d="M23 26 L28 26" />
      <path d="M36 26 L41 26" />
      <circle cx="26" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 31 L31 35 L33 35" />
      <path d="M28 38 Q32 40 36 38" />
      <path d="M17 30 Q15 32 17 34" />
      <path d="M47 30 Q49 32 47 34" />
    </g>
  ),

  // bowl cut + chunky square glasses
  iris: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 18 32 18 Q44 18 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* bowl cut: flat fringe + sides */}
      <path d="M18 24 L18 14 Q32 8 46 14 L46 24" />
      <path d="M18 24 L46 24" />
      {/* brows above glasses */}
      <path d="M23 27 L29 27" />
      <path d="M35 27 L41 27" />
      {/* square glasses */}
      <rect x="22" y="29" width="8" height="6" />
      <rect x="34" y="29" width="8" height="6" />
      <path d="M30 32 L34 32" />
      <path d="M32 35 Q31 38 32 39" />
      <path d="M29 42 L35 42" />
      <path d="M19 32 Q17 34 19 36" />
      <path d="M45 32 Q47 34 45 36" />
    </g>
  ),

  // top knot + jaw beard
  leo: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 16 32 16 Q44 16 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* hair gathered up */}
      <path d="M20 22 Q22 10 32 10 Q42 10 44 22" />
      <path d="M22 18 Q28 12 32 12" />
      <path d="M42 18 Q36 12 32 12" />
      {/* top knot */}
      <circle cx="32" cy="7" r="3" />
      <path d="M30 6 Q32 9 34 6" />
      {/* beard along jaw */}
      <path d="M22 36 Q24 44 32 46 Q40 44 42 36" />
      <path d="M22 36 L24 38" />
      <path d="M42 36 L40 38" />
      <path d="M28 42 L28 45" />
      <path d="M36 42 L36 45" />
      <path d="M23 28 L28 27" />
      <path d="M36 27 L41 28" />
      <circle cx="26" cy="30" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="30" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 32 Q31 35 32 36" />
      <path d="M29 39 Q32 40 35 39" />
    </g>
  ),

  // wrapped headscarf / turban
  wren: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 22 32 22 Q44 22 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* wrap layers */}
      <path d="M18 22 Q18 10 32 8 Q46 10 46 22" />
      <path d="M18 18 Q26 14 36 14 Q44 14 46 18" />
      <path d="M20 14 Q28 11 38 12" />
      {/* fold seam */}
      <path d="M36 16 L42 22" />
      <path d="M24 28 Q26 27 28 28" />
      <path d="M36 28 Q38 27 40 28" />
      <circle cx="26" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 33 Q31 36 32 37" />
      <path d="M28 40 Q32 42 36 40" />
      <path d="M45 32 Q47 34 45 36" />
    </g>
  ),

  // smiling eyes above a surgical mask
  kai: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M18 30 Q18 14 32 14 Q46 14 46 30 Q46 42 32 44 Q18 42 18 30 Z" />
      {/* short hair */}
      <path d="M18 22 Q22 10 32 11 Q42 10 46 22" />
      <path d="M22 16 L28 22" />
      <path d="M32 13 L34 22" />
      <path d="M40 16 L40 22" />
      <path d="M23 26 L28 26" />
      <path d="M36 26 L41 26" />
      {/* smiling/curved eyes */}
      <path d="M24 29 Q26 28 28 29" />
      <path d="M36 29 Q38 28 40 29" />
      {/* mask body */}
      <path d="M21 34 L43 34" />
      <path d="M21 34 Q22 41 32 42 Q42 41 43 34" />
      {/* ear loops */}
      <path d="M21 34 Q17 32 17 30" />
      <path d="M43 34 Q47 32 47 30" />
      {/* pleats */}
      <path d="M24 37 L40 37" />
      <path d="M24 39 L40 39" />
      <path d="M17 30 Q15 32 17 34" />
      <path d="M47 30 Q49 32 47 34" />
    </g>
  ),

  // locs / dreads
  zion: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 18 32 18 Q44 18 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      <path d="M20 24 Q22 14 32 14 Q42 14 44 24" />
      {/* loc strands hanging past jaw */}
      <path d="M18 24 L16 40" />
      <path d="M22 24 L21 42" />
      <path d="M26 24 L26 44" />
      <path d="M38 24 L38 44" />
      <path d="M42 24 L43 42" />
      <path d="M46 24 L48 40" />
      {/* short locs on top */}
      <path d="M24 20 L25 14" />
      <path d="M30 18 L30 12" />
      <path d="M36 18 L36 12" />
      <path d="M40 20 L39 14" />
      {/* loc segment ticks */}
      <path d="M21 32 L23 32" />
      <path d="M26 34 L28 34" />
      <path d="M40 32 L42 32" />
      <path d="M36 34 L38 34" />
      <path d="M24 28 Q26 27 28 28" />
      <path d="M36 28 Q38 27 40 28" />
      <circle cx="26" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="31" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 33 Q31 36 32 37" />
      <path d="M28 40 Q32 42 36 40" />
    </g>
  ),

  // bowler hat + handlebar mustache
  otto: (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 60 Q22 48 32 48 Q42 48 54 60" />
      <path d="M27 50 L27 44 M37 50 L37 44" />
      <path d="M20 32 Q20 20 32 20 Q44 20 44 32 Q44 42 32 44 Q20 42 20 32 Z" />
      {/* bowler — brim then dome */}
      <path d="M14 22 L50 22" />
      <path d="M18 20 L46 20" />
      <path d="M20 20 Q20 10 32 10 Q44 10 44 20" />
      <path d="M22 14 Q26 11 32 11" />
      <path d="M23 26 Q26 25 29 26" />
      <path d="M35 26 Q38 25 41 26" />
      <circle cx="26" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="38" cy="29" r="0.9" fill="currentColor" stroke="none" />
      <path d="M32 31 L31 35 L33 35" />
      {/* handlebar mustache — curls up at the tips */}
      <path d="M22 38 Q24 36 28 37 Q32 36 36 37 Q40 36 42 38" />
      <path d="M22 38 Q20 36 22 34" />
      <path d="M42 38 Q44 36 42 34" />
      <path d="M30 41 L34 41" />
      <path d="M19 32 Q17 34 19 36" />
      <path d="M45 32 Q47 34 45 36" />
    </g>
  ),
};

const SHAPE_KEYS = Object.keys(SHAPES) as Array<keyof typeof SHAPES>;

interface Tint {
  bg: string;
  stroke: string;
}

// One tint per AGENT_BRAND_COLORS entry. Stroke is the brand color; bg is a
// soft companion (approximately the stroke at ~10% over white) so the line
// art reads clearly while staying on-brand.
const BRAND_TINT_BG = [
  "#efeafe", // manifold
  "#e3e0ed", // deep_space (very dark — needs a slightly more colored wash)
  "#f4eefd", // moonglow
  "#ffe6da", // forge_fire
  "#dfeafa", // hot_blue
  "#ebebec", // graphite
];

// Moonglow (#C5B4E3) is too pale to be a stroke; pair it with a darker
// brand-adjacent purple so the line art remains legible on the soft bg.
const BRAND_STROKE_OVERRIDES: Record<string, string> = {
  "#C5B4E3": "#7e6cd1",
};

const TINTS: Tint[] = AGENT_BRAND_COLORS.map((color, i) => ({
  bg: BRAND_TINT_BG[i % BRAND_TINT_BG.length],
  stroke: BRAND_STROKE_OVERRIDES[color] ?? color,
}));

const GRAYSCALE: Tint = { bg: "#eeeeef", stroke: "#424242" };

// Independent axis layered onto the base shape+tint pairing so two agents are
// far less likely to render alike.
const CONTAINER_RADII = [0.28, 0.5, 0.12] as const; // squircle, circle, soft square

// djb2 — small, deterministic, good enough for bucketing.
function hash(input: string): number {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function shapeFor(seed: string): ReactNode {
  return SHAPES[SHAPE_KEYS[hash(seed) % SHAPE_KEYS.length]];
}

function tintFor(seed: string): Tint {
  // Offset by a second hash so the same seed doesn't always pair shape N with tint N.
  return TINTS[hash(seed + "#tint") % TINTS.length];
}

function containerRadiusFor(seed: string): number {
  return CONTAINER_RADII[hash(seed + "#container") % CONTAINER_RADII.length];
}

interface AgentAvatarProps {
  /** Agent id — preferred seed. */
  id?: string | null;
  /** Agent name — fallback seed when id is missing. */
  name?: string | null;
  /** Pixel size of the rendered square. */
  size?: number;
  /** Render without the tinted background pill. */
  withBg?: boolean;
  /** Force grayscale styling regardless of seed. */
  grayscale?: boolean;
  /** Override the stroke width — defaults scale with size. */
  strokeWidth?: number;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
  title?: string;
}

export const AgentAvatar = ({
  id,
  name,
  size = 44,
  withBg: _withBg = true,
  grayscale = false,
  strokeWidth: _strokeWidth,
  className,
  title,
  "aria-hidden": ariaHidden,
}: AgentAvatarProps) => {
  const seed = id || name || "";
  const palette = grayscale ? BORING_GRAYSCALE : AGENT_BRAND_COLORS;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        className
      )}
      style={{ width: size, height: size }}
      aria-hidden={ariaHidden}
      title={title}
    >
      <BoringAvatar name={seed} size={size} variant="beam" colors={palette} />
    </span>
  );
};
